import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { Switch } from '@/components/ui/switch';
import { iconChevronDown, iconChevronRight, iconPlus } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { usePanelSectionOpen } from './use-panel-section.hook';

/**
 * The agent panel's controls in Gumloop's panel style (measured on an agent
 * workspace at gumloop.com). Pixel values throughout: the app's root font is
 * 17 to 19px, so rem sizes run large.
 */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement>;

const BUTTON_VARIANT = {
  outline:
    'border-border bg-background text-foreground hover:bg-muted data-[state=open]:bg-muted',
  dark: '[--cl-base:currentColor] border-[#262626] bg-[linear-gradient(180deg,#434343_0%,#262626_100%)] text-white shadow-[inset_0_1.5px_0_0_rgba(255,255,255,0.24)] hover:opacity-90 dark:border-[#e5e5e5] dark:bg-[linear-gradient(180deg,#ffffff_0%,#e4e4e4_100%)] dark:text-[#171717] dark:shadow-[inset_0_1.5px_0_0_rgba(255,255,255,0.6)]',
  ghost:
    'border-transparent bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground data-[state=open]:bg-muted',
} as const;

/** Gumloop's 28px header button (Save): outline, dark, or ghost. */
export const PanelButton = forwardRef<
  HTMLButtonElement,
  ButtonProps & { variant?: keyof typeof BUTTON_VARIANT }
>(function PanelButton(
  { variant = 'outline', className, children, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type='button'
      className={cn(
        'inline-flex h-[28px] shrink-0 items-center justify-center gap-[6px] rounded-[6px] border px-[12px] text-[12px] leading-[16px] font-medium whitespace-nowrap outline-hidden transition-[opacity,background-color,color] focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
        BUTTON_VARIANT[variant],
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

/** A 28px ghost button holding one icon (back, undo, a row's kebab). */
export const PanelIconButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function PanelIconButton({ className, children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type='button'
        className={cn(
          'inline-flex size-[28px] shrink-0 items-center justify-center rounded-[6px] text-muted-foreground outline-hidden transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40 data-[state=open]:bg-muted data-[state=open]:text-foreground',
          className
        )}
        {...rest}
      >
        {children}
      </button>
    );
  }
);

/** Gumloop's 24px Add button on a section's header row. */
export const SectionAddButton = forwardRef<
  HTMLButtonElement,
  ButtonProps & { label?: string }
>(function SectionAddButton({ label = 'Add', className, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type='button'
      className={cn(
        'inline-flex h-[24px] shrink-0 items-center gap-[6px] rounded-[6px] border border-border bg-background px-[6px] text-[12px] leading-[16px] font-medium text-foreground outline-hidden transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-muted',
        className
      )}
      {...rest}
    >
      <AppIcon icon={iconPlus} style={{ fontSize: 12 }} />
      {label}
    </button>
  );
});

/**
 * A section of the panel: Gumloop's 36px muted header row (the name, a count,
 * the chevron, and actions at the right), sticky while its section scrolls,
 * collapsing from the row. The open state is remembered per section.
 */
export function PanelSection({
  id,
  title,
  count,
  actions,
  defaultOpen = true,
  children,
}: {
  /** The key the open state is remembered under. */
  id: string;
  title: string;
  count?: number;
  actions?: ReactNode;
  defaultOpen?: boolean;
  children?: ReactNode;
}) {
  const [open, setOpen] = usePanelSectionOpen(id, defaultOpen);
  const bodyId = useId();
  return (
    <section aria-label={title} className='flex min-w-0 flex-col gap-[8px]'>
      <div className='sticky top-0 z-10 bg-background pt-[4px]'>
        <div className='group/section relative flex min-h-[36px] items-center gap-[8px] rounded-[10px] bg-muted px-[12px] transition-colors select-none hover:bg-muted-foreground/[0.12]'>
          <button
            type='button'
            aria-expanded={open}
            aria-controls={bodyId}
            aria-label={title}
            onClick={() => setOpen(!open)}
            className='absolute inset-0 rounded-[10px] outline-hidden focus-visible:ring-2 focus-visible:ring-ring'
          />
          <span className='pointer-events-none truncate text-[14px] leading-[20px] font-medium text-foreground'>
            {title}
          </span>
          {count !== undefined && (
            <span className='pointer-events-none shrink-0 text-[14px] leading-[20px] text-muted-foreground tabular-nums'>
              {count}
            </span>
          )}
          <AppIcon
            icon={iconChevronDown}
            aria-hidden
            className={cn(
              'pointer-events-none shrink-0 text-muted-foreground/50 transition-[transform,color] duration-200 group-hover/section:text-muted-foreground',
              !open && '-rotate-90'
            )}
            style={{ fontSize: 11 }}
          />
          {actions && (
            <div className='relative z-[1] -mr-[6px] ml-auto flex shrink-0 items-center gap-[4px]'>
              {actions}
            </div>
          )}
        </div>
      </div>
      {open && (
        <div id={bodyId} className='flex min-w-0 flex-col'>
          {children}
        </div>
      )}
    </section>
  );
}

/** Gumloop's switch: 36 by 20, a 16px thumb, primary when on. */
export function PanelSwitch({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Switch>) {
  return (
    <Switch
      className={cn(
        'h-[20px] w-[36px] shadow-none [&>span]:h-[16px] [&>span]:w-[16px] [&>span[data-state=checked]]:translate-x-[16px]',
        className
      )}
      {...props}
    />
  );
}

/** The 24px box a row's 16px icon sits in. */
function RowIcon({ icon }: { icon: IconDefinition }) {
  return (
    <span className='flex size-[24px] shrink-0 items-center justify-center text-foreground'>
      <AppIcon icon={icon} style={{ fontSize: 15 }} />
    </span>
  );
}

/**
 * Gumloop's toggle row (Edit Own Instructions): an icon, the label and a
 * switch at the right; the whole row toggles it.
 */
export function ToggleRow({
  icon,
  label,
  checked,
  disabled,
  onChange,
  children,
}: {
  icon: IconDefinition;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  /** Between the label and the switch (an Open link). */
  children?: ReactNode;
}) {
  const id = useId();
  return (
    <div
      className={cn(
        'group/row flex min-h-[34px] items-center gap-[8px] rounded-[10px] px-[4px] py-[4px] transition-colors',
        !disabled && 'cursor-pointer hover:bg-muted'
      )}
      onClick={(event) => {
        if (disabled) return;
        // The switch and any control inside handle their own clicks.
        if ((event.target as Element).closest('button, a, [role=switch]'))
          return;
        onChange(!checked);
      }}
    >
      <RowIcon icon={icon} />
      <label
        htmlFor={id}
        className='min-w-0 flex-1 cursor-[inherit] truncate text-[14px] leading-[20px] font-medium text-foreground'
        onClick={(event) => event.preventDefault()}
      >
        {label}
      </label>
      {children}
      <PanelSwitch
        id={id}
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
    </div>
  );
}

/**
 * Gumloop's list row (a connector): a 24px tile with the logo or icon, the
 * name and one muted line, and actions at the right. The row opens its
 * details when it has any.
 */
export function ListRow({
  leading,
  title,
  line,
  actions,
  onOpen,
  label,
}: {
  leading: ReactNode;
  title: ReactNode;
  line?: ReactNode;
  actions?: ReactNode;
  onOpen?: () => void;
  /** The accessible name of the open action. */
  label?: string;
}) {
  return (
    <div className='group/row relative flex min-h-[48px] items-center gap-[8px] rounded-[10px] px-[4px] py-[6px] transition-colors hover:bg-muted'>
      {onOpen && (
        <button
          type='button'
          aria-label={label}
          onClick={onOpen}
          className='absolute inset-0 rounded-[10px] outline-hidden focus-visible:ring-2 focus-visible:ring-ring'
        />
      )}
      <span className='pointer-events-none flex size-[24px] shrink-0 items-center justify-center rounded-[4px] bg-muted p-[4px]'>
        {leading}
      </span>
      <span className='pointer-events-none flex min-w-0 flex-1 flex-col'>
        <span className='truncate text-[14px] leading-[20px] font-medium text-foreground'>
          {title}
        </span>
        {line && (
          <span className='truncate text-[12px] leading-[16px] text-muted-foreground'>
            {line}
          </span>
        )}
      </span>
      {actions && (
        <span className='relative z-[1] flex shrink-0 items-center gap-[4px]'>
          {actions}
        </span>
      )}
    </div>
  );
}

/** Gumloop's View all row: muted words and a chevron at the right. */
export function ViewAllRow({
  label = 'View all',
  onClick,
}: {
  label?: string;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className='flex min-h-[40px] w-full items-center gap-[8px] rounded-[10px] px-[4px] py-[6px] text-left text-[14px] leading-[20px] text-muted-foreground outline-hidden transition-colors hover:bg-muted-foreground/[0.08] focus-visible:ring-2 focus-visible:ring-ring'
    >
      <span className='min-w-0 flex-1 truncate'>{label}</span>
      <span className='flex size-[28px] items-center justify-center'>
        <AppIcon icon={iconChevronRight} style={{ fontSize: 12 }} />
      </span>
    </button>
  );
}

/** A section's empty state, with optional detail about its current state. */
export function PanelEmpty({ title, line }: { title: string; line?: string }) {
  return (
    <div className='flex flex-col gap-[4px] p-[12px]'>
      <p className='text-[14px] leading-[20px] font-medium text-foreground'>
        {title}
      </p>
      {line && (
        <p className='text-[14px] leading-[20px] text-muted-foreground'>
          {line}
        </p>
      )}
    </div>
  );
}

/** A group's small label inside a section (Messages, Data). */
export function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <p className='px-[4px] pt-[8px] pb-[4px] text-[12px] leading-[16px] font-medium text-muted-foreground'>
      {children}
    </p>
  );
}

/** A field's name above it: 14px on 20px, weight 500. */
export function FieldLabel({
  htmlFor,
  icon,
  children,
}: {
  htmlFor?: string;
  /** An icon or an app's logo before the label (`LabelIcon`, `ConnectionLogo`). */
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className='flex items-center gap-[6px] px-[4px] pb-[6px] text-[14px] leading-[20px] font-medium text-foreground'
    >
      {icon ? <LabelIconSlot>{icon}</LabelIconSlot> : null}
      {children}
    </label>
  );
}

/** A field label's icon: 12px, muted, in a 14px slot so labels line up. */
export function LabelIcon({ icon }: { icon: IconDefinition }) {
  return <AppIcon icon={icon} style={{ fontSize: 12 }} />;
}

/** The 14px slot a label's icon or logo sits in. */
export function LabelIconSlot({ children }: { children: ReactNode }) {
  return (
    <span
      aria-hidden='true'
      className='flex size-[14px] shrink-0 items-center justify-center text-muted-foreground'
    >
      {children}
    </span>
  );
}

/** A drill-in's section title: 16px on 24px, weight 500. */
export function DetailTitle({
  children,
  actions,
}: {
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className='flex min-h-[28px] items-center gap-[8px] pb-[8px]'>
      <h3 className='min-w-0 flex-1 truncate text-[16px] leading-[24px] font-medium text-foreground'>
        {children}
      </h3>
      {actions}
    </div>
  );
}

/** Gumloop's stat tile: the value at 18px 600, the label under it. */
export function StatTile({
  value,
  label,
}: {
  value: ReactNode;
  label: string;
}) {
  return (
    <div className='flex min-w-0 flex-1 flex-col gap-[4px] rounded-[8px] border border-border p-[8px]'>
      <span className='truncate text-[18px] leading-[28px] font-semibold text-foreground tabular-nums'>
        {value}
      </span>
      <span className='truncate text-[12px] leading-[16px] font-medium text-muted-foreground'>
        {label}
      </span>
    </div>
  );
}

export interface Crumb {
  label: string;
  onClick?: () => void;
}

/**
 * Gumloop's breadcrumb, where the tabs are while a drill-in is open: earlier
 * parts muted and clickable, the last one in the text colour.
 */
export function Breadcrumb({ crumbs }: { crumbs: readonly Crumb[] }) {
  return (
    <nav aria-label='Breadcrumb' className='min-w-0'>
      <ol className='flex min-w-0 items-center gap-[6px] text-[14px] leading-[20px] text-muted-foreground'>
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <li
              key={crumb.label + index}
              className={cn(
                'flex min-w-0 items-center gap-[6px]',
                !last && 'max-w-[40%] shrink-0'
              )}
            >
              {index > 0 && (
                <AppIcon
                  icon={iconChevronRight}
                  aria-hidden
                  className='shrink-0'
                  style={{ fontSize: 11 }}
                />
              )}
              {last || !crumb.onClick ? (
                <span
                  aria-current={last ? 'page' : undefined}
                  className={cn('min-w-0 truncate', last && 'text-foreground')}
                >
                  {crumb.label}
                </span>
              ) : (
                <button
                  type='button'
                  onClick={crumb.onClick}
                  className='min-w-0 truncate rounded-[4px] outline-hidden transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring'
                >
                  {crumb.label}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
